import React, { useContext, memo, useState, useEffect, useRef, useMemo } from "react";
import {
  Marker,
  MarkerClusterer
} from "@react-google-maps/api";
import axios from "axios";
import { toast } from "react-toastify";
import { EdotCoMultipleSiteIconMapper, SEARCH_AREA_STATES, SiteType, clusterDefaultZoomLvl, googleClusterImgUrl, siteEdotcoTowerSvg } from "../../../../constants/WebMap";
import { WebMapContext } from "../WebMapGoogleMap";
import { filterEdotcoSiteTowers, getTooltipDataForEdotcoTower } from "../../../../services/api/siteService";
import { convertArrayToStringWithDelimeter } from "../../../../utils/helper";
import { paginatedApiInvoker } from "../../../../utils/webMapApiHelper";

const EdotcoSiteIcon = ({ idx, iconData: item, scale, operatorDetails, onClickEdotcoSiteTower, mapTool, cluster = null, type }) => {
  return (
    <>
      {item && item.customersOnSite && item.customersOnSite.length > 0
        ? <Marker
          key={idx || 1}
          position={{ lat: item.latitude, lng: item.longitude }}
          onLoad={(marker) => {
            const customIcon = (opts) =>
              item.customersOnSite.length > 1
                ? {
                  url: (scale === 1.10 || scale === 1.1) ? EdotCoMultipleSiteIconMapper.e_co_existing_2x :
                    EdotCoMultipleSiteIconMapper[
                    item.status
                      ? item.status
                        .trim()
                        .replace(/[\s-\.]+/g, "_")
                        .toLowerCase()
                      : "default"
                    ],
                  anchor: new window.google.maps.Point(14, 28),
                  animation: window.google.maps.Animation.DROP,
                }
                : Object.assign(
                  {
                    path: siteEdotcoTowerSvg[
                      item.status
                        ? item.status
                          .trim()
                          .replace(/[\s-\.]+/g, "_")
                          .toLowerCase()
                        : "default"
                    ],
                    fillOpacity: 1,
                    strokeWeight: 0.7,
                    scale: scale || 0.75,
                    anchor: new window.google.maps.Point(20, 34),
                    animation: window.google.maps.Animation.DROP,
                  },
                  opts
                );
            const operator = operatorDetails.find(
              (x) => x.operator === item.customersOnSite[0]
            );

            const oprtColor = operator?.isAnonymized
              ? "#707070"
              : operator?.is_others
                ? "#01753b"
                : operator?.color || "grey"

            marker.setIcon(
              customIcon({
                fillColor: oprtColor,
                strokeColor: "white",
              })
            );
          }}
          onClick={() => {
            onClickEdotcoSiteTower({
              lat: item.latitude,
              long: item.longitude,
              oprt: item.customersOnSite[0],
              by: type
            });
          }}
          options={{
            clickable: mapTool ? false : true
          }}
          clusterer={cluster}
        />
        : <></>}
    </>
  );
}

const EdotcoSitesSection = ({
  selectedSiteRef,
  mapTool,
  edotcoSubFilters = { status: [], action: [] },
  edotcoSiteVisible,
  searchAreaBtn,
  setSearchAreaBtn,
  tooltipData,
  setEdotcoSiteTooltipData,
  setApisOngoing,
  saveLastBound,
  operatorsPermittedECSiteET,
}) => {
  const cancelToken = useRef();
  let timeOutForApiCall = 600;
  const timeoutForEdotcoSiteTowers = useRef();
  const {
    eyeIcons,
    siteZoom,
    operatorDetails,
    mapZoomLevel,
    selectedEdotcoSiteOperator,
    edotcoSiteAreaToggle,
    geohashNE,
    geohashSW,
    appliedFilters,
    boundsChanged,
    getBbox,
    geohashZoom,
    setDownloadData,
  } = useContext(WebMapContext);
  const [edotcoSiteTowers, setEdotcoSiteTowers] = useState([]);

  useEffect(() => {
    if (mapZoomLevel < Math.min(siteZoom, clusterDefaultZoomLvl)) resetData();
  }, [mapZoomLevel, siteZoom]);

  useEffect(() => {
    resetData();
  }, [appliedFilters.country, appliedFilters.year, appliedFilters.quarter]);

  useEffect(() => {
    if (edotcoSiteAreaToggle) resetData();
  }, [appliedFilters.level, appliedFilters.levelVal]);

  useEffect(() => {
    if (tooltipData && tooltipData.siteType === SiteType.EdotcoSite) {
      setEdotcoSiteTooltipData()
    }
  }, [edotcoSiteTowers.length]);

  useEffect(() => {
    if (tooltipData && tooltipData.siteType === SiteType.Edotco_SiteRef) {
      setEdotcoSiteTooltipData()
    }
  }, [selectedSiteRef]);

  useEffect(() => {
    if (common_cond) {
      if (selectedEdotcoSiteOperator.operatorList?.length <= 1 && selectedEdotcoSiteOperator.value) return; // prevent api call if logic present but no multi oprts
      filterEdotcoSites();
      saveDataLoadedBbox();
    } else if (!eyeIcons.sites || !edotcoSiteVisible) resetData();
    // eslint-disable-next-line
  }, [
    eyeIcons.sites,
    edotcoSubFilters.status,
    edotcoSubFilters.action,
    edotcoSubFilters.dataType,
    edotcoSubFilters.connectionType,
    selectedEdotcoSiteOperator,
    appliedFilters,
    edotcoSiteVisible,
    geohashSW,
    edotcoSiteAreaToggle,
    siteZoom,
    boundsChanged
  ]);

  useEffect(() => {
    if (common_cond && searchAreaBtn === SEARCH_AREA_STATES.FETCH) {
      filterEdotcoSites();
      saveDataLoadedBbox();
    }
    // eslint-disable-next-line
  }, [searchAreaBtn]);

  const common_cond = useMemo(() => {
    return eyeIcons.sites && edotcoSiteVisible && (mapZoomLevel >= siteZoom || mapZoomLevel >= clusterDefaultZoomLvl) &&
      edotcoSubFilters.status.length > 0 && edotcoSubFilters.action.length > 0;
  }, [eyeIcons.sites, edotcoSiteVisible, mapZoomLevel, siteZoom, edotcoSubFilters, appliedFilters.quarter, appliedFilters.year]);

  const resetData = () => {
    setEdotcoSiteTowers([]);
  }

  const saveDataLoadedBbox = () => {
    const { supply, demand, capacity } = eyeIcons;
    // save data loaded bounds
    // do not save when any geo section on & map is about to load geohashes
    if (!((supply || demand || capacity) && geohashZoom && (mapZoomLevel === geohashZoom - 1))) saveLastBound();
  }

  const filterEdotcoSites = () => {
    setEdotcoSiteTowers([]);
    if (typeof cancelToken.current != typeof undefined) {
      cancelToken.current.cancel("Operation canceled due to new request.");
    }
    
    //Save the cancel token for the current request
    cancelToken.current = axios.CancelToken.source();
    clearTimeout(timeoutForEdotcoSiteTowers.current);

    timeoutForEdotcoSiteTowers.current = setTimeout(async () => {
      try {
        const checkedStatus = edotcoSubFilters.status
          .map((item) => {
            if (item.isChecked) return item.filterName;
            return null;
          })
          .filter((item) => item);

        const checkedActions = edotcoSubFilters.action
          .map((item) => {
            if (item.isChecked) return item.filterName;
            return null;
          })
          .filter((item) => item);
        const { neBound, swBound } = getBbox;
        let operatorList = selectedEdotcoSiteOperator.operatorList;



        const payload = {
          ...appliedFilters,
          adL:
            edotcoSiteAreaToggle && appliedFilters.level !== "geoHashLevel"
              ? appliedFilters.level
              : null,
          adLVal:
            edotcoSiteAreaToggle && appliedFilters.level !== "geoHashLevel"
              ? appliedFilters.levelVal
              : null,
          operators: convertArrayToStringWithDelimeter(
            operatorList,
            "_"
          ),
          logicGate: selectedEdotcoSiteOperator.value,
          dataType: edotcoSubFilters?.dataType,
          connectionType: edotcoSubFilters?.connectionType,
          status: convertArrayToStringWithDelimeter(checkedStatus, "_"),
          action: convertArrayToStringWithDelimeter(checkedActions, "_"),
          northEast: convertArrayToStringWithDelimeter(
            edotcoSiteAreaToggle && geohashNE.length ? geohashNE : neBound,
            "_"
          ),
          southWest: convertArrayToStringWithDelimeter(
            edotcoSiteAreaToggle && geohashSW.length ? geohashSW : swBound,
            "_"
          ),
        };
        
        await paginatedApiInvoker(
          (pageSize, page) => filterEdotcoSiteTowers(payload, cancelToken, pageSize, page),
          40000, // perPage
          () => setApisOngoing(count => count + 1),
          (data) => {
            if (data) {
              setEdotcoSiteTowers((prevState) => [...prevState, ...data]);
            }
          },
          () => {
             //Saving filters for download
             setDownloadData((prev)=>({
               ...prev,
               sitesEdotco: {
                 ...payload,
                 cancelToken: cancelToken,
               },
             }));
            if (!appliedFilters.level) setSearchAreaBtn(SEARCH_AREA_STATES.ENABLED);
            setApisOngoing(count => count - 1);
          }
        );
      } catch (err) {
        console.log("Error while filtering edot co site towers", err);
        toast.error(err.message, { theme: "colored" });
      }
    }, timeOutForApiCall);
  };

  const onClickEdotcoSiteTower = async (params) => {
    getTooltipDataForEdotcoTower({
      countryCode: appliedFilters.countryCode,
      quarter: appliedFilters.quarter,
      year: appliedFilters.year,
      operator: params.oprt,
      lat: params.lat,
      long: params.long,
    })
      .then(({ success, data }) => {
        if (success && data) {
          setEdotcoSiteTooltipData({ ...data, siteType: params.by });
        } else {
          toast.error(data, { theme: "colored" });
        }
      })
      .catch((err) => {
        console.log(
          "Error wihle getting tool tip data for EDOTCO site tower",
          err
        );
        toast.error(err.message, { theme: "colored" });
      });
  };

  return (
    eyeIcons.sites && edotcoSiteVisible
      ? <>
        {edotcoSiteTowers.length > 0
          ? <div className="data-on-map">
            {mapZoomLevel >= siteZoom
              ? edotcoSiteTowers.map((item, idx) => {
                const isSelectedSiteRef = selectedSiteRef.find(obj => obj.siteRef === item.siteRef);
                if (isSelectedSiteRef) return (<></>);
                else return (
                  <EdotcoSiteIcon
                    idx={idx + "_" + item?.siteRef + "_edotco"}
                    iconData={item}
                    operatorDetails={operatorsPermittedECSiteET}
                    onClickEdotcoSiteTower={onClickEdotcoSiteTower}
                    mapTool={mapTool}
                    type={SiteType.EdotcoSite}
                  />
                )
              })
              : mapZoomLevel >= clusterDefaultZoomLvl
                ? <MarkerClusterer options={{ imagePath: googleClusterImgUrl }}>
                  {(clusterer) => {
                    return (
                      <>{edotcoSiteTowers.map((item, idx) => {
                        const isSelectedSiteRef = selectedSiteRef.find(obj => obj.siteRef === item.siteRef);
                        if (isSelectedSiteRef) return (<></>);
                        else return (
                          <EdotcoSiteIcon
                            idx={idx}
                            iconData={item}
                            operatorDetails={operatorsPermittedECSiteET}
                            onClickEdotcoSiteTower={onClickEdotcoSiteTower}
                            mapTool={mapTool}
                            cluster={clusterer}
                            type={SiteType.EdotcoSite}
                          />
                        )
                      })}</>
                    )
                  }}
                </MarkerClusterer>
                : <></>}
          </div>
          : <></>}
        {selectedSiteRef.length > 0
          ? <div className="data-on-map">
            {mapZoomLevel >= siteZoom
              ? selectedSiteRef.map((item, idx) => (
                <EdotcoSiteIcon
                  idx={idx}
                  iconData={item}
                  operatorDetails={operatorsPermittedECSiteET}
                  onClickEdotcoSiteTower={onClickEdotcoSiteTower}
                  mapTool={mapTool}
                  scale={1.10}
                  type={SiteType.Edotco_SiteRef}
                />
              )) : <MarkerClusterer options={{ imagePath: googleClusterImgUrl }}>
                {(clusterer) => {
                  return (
                    <>{selectedSiteRef.map((item, idx) => (
                      <EdotcoSiteIcon
                        idx={idx}
                        iconData={item}
                        operatorDetails={operatorsPermittedECSiteET}
                        onClickEdotcoSiteTower={onClickEdotcoSiteTower}
                        mapTool={mapTool}
                        scale={1.10}
                        cluster={clusterer}
                        type={SiteType.Edotco_SiteRef}
                      />
                    ))}</>
                  )
                }}
              </MarkerClusterer>}
          </div>
          : <></>}
      </>
      : <></>
  )
};

export default memo(EdotcoSitesSection);